"use client"

import { useState, useRef, useEffect } from "react"
import { Volume2 } from "lucide-react"
import { detectSpeakers } from "../utils/mediaDetection.js"
import { toast } from "react-toastify"

export function SpeakerTest() {
  const [state, setState] = useState({
    availableSpeakers: [],
    selectedSpeaker: "",
    volume: 50,
    isTesting: false,
  })

  const audioContextRef = useRef(null)
  const oscillatorRef = useRef(null)

  const loadSpeakers = async () => {
    try {
      const speakers = await detectSpeakers()
      const defaultSpeaker = speakers.find(s => s.deviceType === 'default') || speakers?.[0]

      setState(prev => ({
        ...prev,
        availableSpeakers: speakers,
        selectedSpeaker: prev?.selectedSpeaker || defaultSpeaker?.id || "",
      }))
    } catch (error) {
      console.error("Error detecting speakers:", error)
      toast.error("Unable to detect speakers.")
    }
  }

  const testSpeaker = async () => {
    if (oscillatorRef?.current) return

    setState(prev => ({ ...prev, isTesting: true }))
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      audioContextRef.current = audioContext

      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      oscillatorRef.current = oscillator

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      const selected = state?.selectedSpeaker
      if (selected && audioContext.setSinkId) {
        try {
          await audioContext.setSinkId(selected)
        } catch (err) {
          console.warn("SinkId error:", err)
          toast.warning("Could not set selected speaker.")
        }
      }

      gainNode.gain.value = (state?.volume || 0) / 100
      oscillator.frequency.value = 440
      oscillator.type = "sine"
      oscillator.start()
      setTimeout(() => {
        stopTest()
      }, 2000)
    } catch (err) {
      console.error("Speaker test error:", err)
      toast.error("Failed to test speaker.")
      setState(prev => ({ ...prev, isTesting: false }))
    }
  }

  const stopTest = () => {
    try {
      oscillatorRef?.current?.stop?.()
      oscillatorRef.current = null

      audioContextRef?.current?.close?.()
      audioContextRef.current = null
    } catch (err) {
      console.warn("Error stopping audio context:", err)
    }
    setState(prev => ({ ...prev, isTesting: false }))
  }

  useEffect(() => {
    loadSpeakers()
    return () => stopTest()
  }, [])

  useEffect(() => {
    const handleDeviceChange = () => loadSpeakers()
    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange)
    return () => navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange)
  }, [])

  return (
    <div className="device-group">
      <div className="device-label">Speaker</div>

      <div className="device-options">
        {state?.availableSpeakers?.map(speaker => (
          <label key={speaker?.id} className="radio-option">
            <input
              type="radio"
              name="speaker"
              value={speaker?.id}
              checked={state?.selectedSpeaker === speaker?.id}
              onChange={(e) => setState(prev => ({ ...prev, selectedSpeaker: e.target.value }))}
              className="radio-input"
            />
            <span className="radio-label">{speaker?.label}</span>
          </label>
        ))}
      </div>

      <div className="volume-control">
        <Volume2 className="volume-icon" />
        <input
          type="range"
          min="0"
          max="100"
          value={state?.volume ?? 0}
          onChange={(e) => setState(prev => ({ ...prev, volume: parseInt(e.target.value) }))}
          className="volume-slider"
        />
        <span className="volume-value">{state?.volume ?? 0}%</span>
      </div>

      <button
        onClick={state?.isTesting ? stopTest : testSpeaker}
        disabled={state?.isTesting && !oscillatorRef?.current}
        className={`test-button ${state?.isTesting ? "testing" : ""}`}
      >
        {state?.isTesting ? "Testing Speaker" : "Test Speaker"}
      </button>
    </div>
  )
}
